import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AuditLog } from '../../types/api';
import { formatAmountExact } from '../../utils/amountFormatter';

interface AuditLogValueDisplayProps {
  log: AuditLog;
}

export function AuditLogValueDisplay({ log }: AuditLogValueDisplayProps) {
  const { t } = useTranslation();

  const formatValue = (value: Record<string, any> | null | undefined, isOld: boolean): ReactNode => {
    if (!value) return null;

    const { target, action } = log;

    // Balance - Recharge
    if (target === 'balance' && action === 'recharge') {
      return (
        <div className="space-y-2">
          {isOld ? (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
              </span>
              <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
                </span>
                <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
              </div>
              {value.amount && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.amount') || 'المبلغ'}:{' '}
                  </span>
                  <span className="text-green-400 font-semibold">
                    +${formatAmountExact(value.amount as number) || '0'}
                  </span>
                </div>
              )}
              {value.method && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.method') || 'طريقة الدفع'}:{' '}
                  </span>
                  <span className="text-neutral-light">
                    {t(`admin.auditLogs.values.paymentMethod.${value.method as string}`) || (value.method as string)}
                  </span>
                </div>
              )}
              {value.transactionId && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.transactionId') || 'رقم المعاملة'}:{' '}
                  </span>
                  <span className="text-neutral-light font-mono text-xs">{value.transactionId as string}</span>
                </div>
              )}
              {value.failureReason && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.failureReason') || 'سبب الفشل'}:{' '}
                  </span>
                  <span className="text-red-400">{value.failureReason as string}</span>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    // User - Balance Update
    if (target === 'user' && action === 'user_balance_update') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.telegramId') || 'معرف التيليجرام'}:{' '}
            </span>
            <span className="text-neutral-light">{value.telegramId as number}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
            </span>
            <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
          </div>
          {!isOld && value.amount && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.amount') || 'المبلغ'}:{' '}
              </span>
              <span
                className={`font-semibold ${
                  (value.amount as number) >= 0 ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {(value.amount as number) >= 0 ? '+' : ''}
                ${formatAmountExact(value.amount as number) || '0'}
              </span>
            </div>
          )}
          {!isOld && value.type && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.type') || 'النوع'}:{' '}
              </span>
              <span className="text-neutral-light">
                {t(`admin.auditLogs.values.balanceUpdateType.${value.type as string}`) || (value.type as string)}
              </span>
            </div>
          )}
        </div>
      );
    }

    // User - Ban Status Update
    if (target === 'user' && action === 'user_ban_status_update') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.telegramId') || 'معرف التيليجرام'}:{' '}
            </span>
            <span className="text-neutral-light">{value.telegramId as number}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.isBanned') || 'حالة الحظر'}:{' '}
            </span>
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                value.isBanned
                  ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                  : 'bg-green-500/20 text-green-400 border border-green-500/50'
              }`}
            >
              {value.isBanned
                ? t('admin.auditLogs.values.banned') || 'محظور'
                : t('admin.auditLogs.values.notBanned') || 'غير محظور'}
            </span>
          </div>
        </div>
      );
    }

    // User - Admin Status Update
    if (target === 'user' && action === 'user_admin_status_update') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.telegramId') || 'معرف التيليجرام'}:{' '}
            </span>
            <span className="text-neutral-light">{value.telegramId as number}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.isAdmin') || 'حالة المدير'}:{' '}
            </span>
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                value.isAdmin
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50'
              }`}
            >
              {value.isAdmin
                ? t('admin.auditLogs.values.admin') || 'مدير'
                : t('admin.auditLogs.values.regular') || 'عادي'}
            </span>
          </div>
        </div>
      );
    }

    // Order - Purchase
    if (target === 'order' && action === 'purchase') {
      return (
        <div className="space-y-2">
          {isOld ? (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
              </span>
              <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
            </div>
          ) : (
            <>
              {value.orderId && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.orderId') || 'رقم الطلب'}:{' '}
                  </span>
                  <span className="text-neutral-light font-mono text-xs">{value.orderId as string}</span>
                </div>
              )}
              {value.providerId && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.providerId') || 'المزود'}:{' '}
                  </span>
                  <span className="text-neutral-light capitalize">
                    {t(`admin.auditLogs.values.provider.${value.providerId as string}`) || (value.providerId as string)}
                  </span>
                </div>
              )}
              {value.service && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.service') || 'الخدمة'}:{' '}
                  </span>
                  <span className="text-neutral-light">{value.service as string}</span>
                </div>
              )}
              {value.amount && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.amount') || 'المبلغ'}:{' '}
                  </span>
                  <span className="text-red-400 font-semibold">
                    -${formatAmountExact(value.amount as number) || '0'}
                  </span>
                </div>
              )}
              {value.balance !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
                  </span>
                  <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
                </div>
              )}
              {value.providerOrderRef && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.providerOrderRef') || 'مرجع الطلب'}:{' '}
                  </span>
                  <span className="text-neutral-light font-mono text-xs">{value.providerOrderRef as string}</span>
                </div>
              )}
              {value.error && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.auditLogs.values.error') || 'خطأ'}:{' '}
                  </span>
                  <span className="text-red-400">{value.error as string}</span>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    // Order - Status Update
    if (target === 'order' && action === 'order_status_update') {
      return (
        <div className="space-y-2">
          {value.orderId && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.orderId') || 'رقم الطلب'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.orderId as string}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-neutral-medium font-medium">
              {t('admin.auditLogs.values.status') || 'الحالة'}:{' '}
            </span>
            <span className="text-neutral-light capitalize">
              {t(`admin.auditLogs.values.orderStatus.${value.status as string}`) || (value.status as string)}
            </span>
          </div>
          {value.providerOrderRef && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.providerOrderRef') || 'مرجع الطلب'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.providerOrderRef as string}</span>
            </div>
          )}
          {value.notes && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.notes') || 'ملاحظات'}:{' '}
              </span>
              <span className="text-neutral-light">{value.notes as string}</span>
            </div>
          )}
        </div>
      );
    }

    // Order - Refund
    if (target === 'order' && action === 'order_refund') {
      return (
        <div className="space-y-2">
          {value.orderId && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.orderId') || 'رقم الطلب'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.orderId as string}</span>
            </div>
          )}
          {value.balance !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.balance') || 'الرصيد'}:{' '}
              </span>
              <span className="text-neutral-light">${formatAmountExact(value.balance as number) || '0'}</span>
            </div>
          )}
          {!isOld && value.refundAmount && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.refundAmount') || 'مبلغ الاسترداد'}:{' '}
              </span>
              <span className="text-green-400 font-semibold">
                +${formatAmountExact(value.refundAmount as number) || '0'}
              </span>
            </div>
          )}
        </div>
      );
    }

    // Order - Refill
    if (target === 'order' && action === 'order_refill') {
      return (
        <div className="space-y-2">
          {value.orderId && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.orderId') || 'رقم الطلب'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.orderId as string}</span>
            </div>
          )}
          {value.refillId && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.refillId') || 'رقم التعبئة'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.refillId as string}</span>
            </div>
          )}
          {value.providerOrderRef && (
            <div className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.providerOrderRef') || 'مرجع الطلب'}:{' '}
              </span>
              <span className="text-neutral-light font-mono text-xs">{value.providerOrderRef as string}</span>
            </div>
          )}
        </div>
      );
    }

    // Provider Config - Create/Update/Delete
    if (target === 'provider_config') {
      const formatProviderConfig = (config: Record<string, unknown>) => {
        const items: JSX.Element[] = [];

        if (config.providerId) {
          items.push(
            <div key="providerId" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.providerId') || 'المزود'}:{' '}
              </span>
              <span className="text-neutral-light capitalize">
                {t(`admin.auditLogs.values.provider.${config.providerId as string}`) || (config.providerId as string)}
              </span>
            </div>
          );
        }

        if (config.endpoints && typeof config.endpoints === 'object') {
          const endpoints = config.endpoints as Record<string, unknown>;
          if (endpoints.baseUrl) {
            items.push(
              <div key="baseUrl" className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.auditLogs.values.baseUrl') || 'رابط API الأساسي'}:{' '}
                </span>
                <span className="text-neutral-light text-xs font-mono">{endpoints.baseUrl as string}</span>
              </div>
            );
          }
        }

        if (config.margins && typeof config.margins === 'object') {
          const margins = config.margins as Record<string, unknown>;
          const marginItems: JSX.Element[] = [];
          Object.entries(margins).forEach(([key, val]) => {
            marginItems.push(
              <div key={key} className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium capitalize">
                  {t(`admin.auditLogs.values.section.${key}`) || key}:{' '}
                </span>
                <span className="text-neutral-light">
                  {((val as number) * 100).toFixed(1)}%
                </span>
              </div>
            );
          });
          if (marginItems.length > 0) {
            items.push(
              <div key="margins" className="space-y-1">
                <span className="text-neutral-medium font-medium">
                  {t('admin.auditLogs.values.margins') || 'الهوامش'}:{' '}
                </span>
                <div className="pl-4 space-y-1">{marginItems}</div>
              </div>
            );
          }
        }

        if (config.apiKeys && typeof config.apiKeys === 'object') {
          items.push(
            <div key="apiKeys" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.apiKeys') || 'مفاتيح API'}:{' '}
              </span>
              <span className="text-neutral-light italic">
                {t('admin.auditLogs.values.encrypted') || '[مشفرة]'}
              </span>
            </div>
          );
        }

        if (config.hasApiKey !== undefined) {
          items.push(
            <div key="hasApiKey" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.hasApiKey') || 'يحتوي على مفتاح API'}:{' '}
              </span>
              <span
                className={`px-2 py-1 rounded text-xs font-semibold ${
                  config.hasApiKey
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'bg-red-500/20 text-red-400 border border-red-500/50'
                }`}
              >
                {config.hasApiKey
                  ? t('common.yes') || 'نعم'
                  : t('common.no') || 'لا'}
              </span>
            </div>
          );
        }

        if (config.maintenance && typeof config.maintenance === 'object') {
          const maintenance = config.maintenance as Record<string, unknown>;
          if (Object.keys(maintenance).length > 0) {
            items.push(
              <div key="maintenance" className="space-y-1">
                <span className="text-neutral-medium font-medium">
                  {t('admin.auditLogs.values.maintenance') || 'الصيانة'}:{' '}
                </span>
                <div className="pl-4 text-xs text-neutral-light">
                  {Object.entries(maintenance).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="capitalize">{key}:</span>
                      <span>{String(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        }

        return items.length > 0 ? <div className="space-y-2">{items}</div> : null;
      };

      return formatProviderConfig(value);
    }

    // Settings - Update
    if (target === 'settings' && action === 'settings_update') {
      const formatSettings = (settings: Record<string, unknown>) => {
        const items: JSX.Element[] = [];

        if (settings.usdRate !== undefined) {
          items.push(
            <div key="usdRate" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.usdRate') || 'سعر صرف الدولار'}:{' '}
              </span>
              <span className="text-neutral-light">{settings.usdRate as number}</span>
            </div>
          );
        }

        if (settings.referralPercent !== undefined) {
          items.push(
            <div key="referralPercent" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.referralPercent') || 'نسبة الإحالة'}:{' '}
              </span>
              <span className="text-neutral-light">{((settings.referralPercent as number) * 100).toFixed(1)}%</span>
            </div>
          );
        }

        if (settings.payments && typeof settings.payments === 'object') {
          const payments = settings.payments as Record<string, unknown>;
          const paymentItems: JSX.Element[] = [];
          Object.entries(payments).forEach(([method, config]) => {
            if (config && typeof config === 'object') {
              const methodConfig = config as Record<string, unknown>;
              const methodItems: JSX.Element[] = [];
              if (methodConfig.walletNumber) {
                methodItems.push(
                  <div key="walletNumber" className="flex items-center gap-2">
                    <span className="text-neutral-medium font-medium text-xs">
                      {t('admin.auditLogs.values.walletNumber') || 'رقم المحفظة'}:{' '}
                    </span>
                    <span className="text-neutral-light text-xs">{methodConfig.walletNumber as string}</span>
                  </div>
                );
              }
              if (methodConfig.merchantId) {
                methodItems.push(
                  <div key="merchantId" className="flex items-center gap-2">
                    <span className="text-neutral-medium font-medium text-xs">
                      {t('admin.auditLogs.values.merchantId') || 'معرف التاجر'}:{' '}
                    </span>
                    <span className="text-neutral-light text-xs font-mono">{methodConfig.merchantId as string}</span>
                  </div>
                );
              }
              if (methodItems.length > 0) {
                paymentItems.push(
                  <div key={method} className="pl-4 space-y-1">
                    <span className="text-neutral-medium font-medium text-xs capitalize">
                      {t(`admin.auditLogs.values.paymentMethod.${method}`) || method}:
                    </span>
                    <div className="pl-2 space-y-1">{methodItems}</div>
                  </div>
                );
              }
            }
          });
          if (paymentItems.length > 0) {
            items.push(
              <div key="payments" className="space-y-1">
                <span className="text-neutral-medium font-medium">
                  {t('admin.auditLogs.values.payments') || 'طرق الدفع'}:{' '}
                </span>
                <div className="pl-4 space-y-1">{paymentItems}</div>
              </div>
            );
          }
        }

        if (settings.welcomeImageUrl !== undefined) {
          items.push(
            <div key="welcomeImageUrl" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.welcomeImageUrl') || 'رابط صورة الترحيب'}:{' '}
              </span>
              <span className="text-neutral-light text-xs break-all">
                {settings.welcomeImageUrl as string || t('common.no') || 'لا'}
              </span>
            </div>
          );
        }

        if (settings.botExplanationChannelUrl !== undefined) {
          items.push(
            <div key="botExplanationChannelUrl" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.botExplanationChannelUrl') || 'رابط قناة الشرح'}:{' '}
              </span>
              <span className="text-neutral-light text-xs break-all">
                {settings.botExplanationChannelUrl as string || t('common.no') || 'لا'}
              </span>
            </div>
          );
        }

        if (settings.maintenanceMode !== undefined) {
          items.push(
            <div key="maintenanceMode" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.maintenanceMode') || 'وضع الصيانة'}:{' '}
              </span>
              <span
                className={`px-2 py-1 rounded text-xs font-semibold ${
                  settings.maintenanceMode
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                    : 'bg-green-500/20 text-green-400 border border-green-500/50'
                }`}
              >
                {settings.maintenanceMode
                  ? t('admin.auditLogs.values.enabled') || 'مفعل'
                  : t('admin.auditLogs.values.disabled') || 'معطل'}
              </span>
            </div>
          );
        }

        if (settings.supportAccountUrl !== undefined) {
          items.push(
            <div key="supportAccountUrl" className="flex items-center gap-2">
              <span className="text-neutral-medium font-medium">
                {t('admin.auditLogs.values.supportAccountUrl') || 'رابط حساب الدعم'}:{' '}
              </span>
              <span className="text-neutral-light text-xs break-all">
                {settings.supportAccountUrl as string || t('common.no') || 'لا'}
              </span>
            </div>
          );
        }

        return items.length > 0 ? <div className="space-y-2">{items}</div> : null;
      };

      return formatSettings(value);
    }

    // Fallback: Display as formatted JSON only if absolutely necessary
    return (
      <div className="text-xs text-neutral-light">
        <pre className="whitespace-pre-wrap max-h-40 overflow-y-auto bg-neutral-dark/50 p-2 rounded">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {log.oldValue && (
        <div className="p-3 bg-neutral-dark/30 rounded-lg border border-neutral-medium/30">
          <div className="text-sm font-medium text-neutral-medium mb-2">
            {t('admin.auditLogs.oldValue') || 'القيمة القديمة'}:
          </div>
          {formatValue(log.oldValue as Record<string, unknown>, true)}
        </div>
      )}
      {log.newValue && (
        <div className="p-3 bg-neutral-dark/30 rounded-lg border border-neutral-medium/30">
          <div className="text-sm font-medium text-neutral-medium mb-2">
            {t('admin.auditLogs.newValue') || 'القيمة الجديدة'}:
          </div>
          {formatValue(log.newValue as Record<string, unknown>, false)}
        </div>
      )}
      {!log.oldValue && !log.newValue && (
        <div className="text-xs text-neutral-medium italic">
          {t('admin.auditLogs.noValue') || 'لا توجد قيم'}
        </div>
      )}
    </div>
  );
}
